/**
 * Product Options JavaScript Module
 * Handles product variant selection, pricing, shipping info, and chat functionality
 */

// Prevent class redeclaration in drawer context
if (typeof window.ProductOptionsManager === "undefined") {
    class ProductOptionsManager {
        constructor(productData, isSneaker = false, productTitle = '', productTags = []) {
            this.productData = productData;
            this.isSneaker = isSneaker;
            this.productTitle = productTitle;
            this.productTags = productTags;
            this.translations =
                window.theme?.translations
                    ?.product_options || {};

            this.currentPath = window.location.pathname;

            // Check if this is a special streetwear product
            this.isSpecialStreetwear = this.checkIfSpecialStreetwear();

            this.initializeElements();
            this.setupEventListeners();
            this.initializeTranslations();

            // Initialize on DOM ready
            if (document.readyState === "loading") {
                document.addEventListener(
                    "DOMContentLoaded",
                    () => this.onDOMReady()
                );
            } else {
                this.onDOMReady();
            }
        }

        checkIfSpecialStreetwear() {
            // Check if product has streetwear tag
            const hasStreetwearTag = this.productTags.some(tag =>
                tag.toLowerCase() === 'streetwear'
            );

            if (!hasStreetwearTag) {
                return false;
            }

            // Check if product name contains essentials or denim tears
            const titleLower = this.productTitle.toLowerCase();
            return titleLower.includes('essentials') || titleLower.includes('denim tears');
        }

        initializeElements() {
            // Helper function to find element in document or drawer
            const findElement = (id) => {
                let element = document.getElementById(id);
                if (!element) {
                    const drawerContent =
                        document.querySelector(
                            "#Product-Drawer-Content"
                        );
                    if (drawerContent) {
                        element =
                            drawerContent.querySelector(
                                `#${id}`
                            );
                    }
                }
                return element;
            };

            this.elements = {
                shippingInfoContainer: findElement(
                    "shipping-info-container"
                ),
                delivery2To14: findElement("delivery2To14"),
                shippingInfoShowMore: findElement(
                    "shippingInfoShowMore"
                ),
                sizeTipSlightSmall: findElement(
                    "sizeTipSlightSmall"
                ),
                sizeTipSlightBig: findElement(
                    "sizeTipSlightBig"
                ),
                sizeTipNormal: findElement("sizeTipNormal"),
                sizeTipVerySmall: findElement(
                    "sizeTipVerySmall"
                ),
                quickContactContainer: findElement(
                    "quickContactContainer"
                ),
                costeReposicionContainer: findElement(
                    "costeReposicionContainer"
                ),
            };
        }

        setupEventListeners() {
            // Use event delegation for dynamic content (drawer context)
            this.clickHandler = (event) => {
                // Shipping info show more toggle
                if (
                    event.target.id ===
                    "shippingInfoShowMore"
                ) {
                    event.preventDefault();
                    this.toggleQuickContact();
                }
            };
            document.addEventListener(
                "click",
                this.clickHandler
            );

            // Event delegation handles all interactions, no need for direct binding
        }

        toggleQuickContact() {
            // Helper function to find element in document or drawer
            const findElement = (id) => {
                let element = document.getElementById(id);
                if (!element) {
                    const drawerContent =
                        document.querySelector(
                            "#Product-Drawer-Content"
                        );
                    if (drawerContent) {
                        element =
                            drawerContent.querySelector(
                                `#${id}`
                            );
                    }
                }

                return element;
            };

            const container = findElement(
                "quickContactContainer"
            );
            const shippingInfoShowMore = findElement(
                "shippingInfoShowMore"
            );

            if (!container || !shippingInfoShowMore) {
                return;
            }

            const isHidden =
                !container.classList.contains("show");

            if (isHidden) {
                container.classList.add("show");
                shippingInfoShowMore.textContent = "Cerrar";
            } else {
                container.classList.remove("show");
                shippingInfoShowMore.textContent = "Info";
            }
        }

        initializeTranslations() {
            // Helper function to find element in document or drawer
            const findElement = (id) => {
                let element = document.getElementById(id);
                if (!element) {
                    const drawerContent =
                        document.querySelector(
                            "#Product-Drawer-Content"
                        );
                    if (drawerContent) {
                        element =
                            drawerContent.querySelector(
                                `#${id}`
                            );
                    }
                }
                return element;
            };

            // Express delivery label - DO NOT override here, let Liquid template handle it
            // The label is set in product-option.liquid based on special_streetwear check
            // Keeping only the font size adjustment
            const spanElement = findElement(
                "expressDeliveryLabel"
            );
            if (spanElement) {
                spanElement.style.fontSize = "14px";
                // DO NOT set innerHTML here - it's handled by Liquid template
            }

            // Discount exception label
            const discountElement = findElement(
                "discountExceptionLabel"
            );
            if (discountElement) {
                discountElement.innerHTML =
                    this.translations.discount_exception ||
                    "*Este producto está excluido de cualquier tipo de cupón, excepto el cupón del newsletter.";
            }

            // No return label
            const noReturnElement =
                findElement("noReturnLabel");
            if (noReturnElement) {
                noReturnElement.innerHTML =
                    this.translations.no_return_exchanges ||
                    "No aceptamos cambios ni devoluciones para este tipo de producto. Al realizar el pedido, aceptas los términos y condiciones.";
            }

            // Size tips
            if (this.elements.sizeTipSlightSmall) {
                this.elements.sizeTipSlightSmall.innerHTML =
                    this.translations
                        .sizing_slightly_small ||
                    "Tallaje: ligeramente pequeño. Recomendamos medio número más grande que tu talla habitual.";
            }
            if (this.elements.sizeTipSlightBig) {
                this.elements.sizeTipSlightBig.innerHTML =
                    this.translations.sizing_slightly_big ||
                    "Tallaje: ligeramente grande. Recomendamos medio número más pequeño que tu talla habitual.";
            }
            if (this.elements.sizeTipNormal) {
                this.elements.sizeTipNormal.innerHTML =
                    this.translations.sizing_normal ||
                    "Tallaje: normal. Recomendamos tu talla habitual.";
            }
            if (this.elements.sizeTipVerySmall) {
                this.elements.sizeTipVerySmall.innerHTML =
                    this.translations.sizing_very_small ||
                    "Tallaje: muy pequeño. Recomendamos un número más grande que tu talla habitual.";
            }

            // Delivery information - Set initial values but don't override shipping logic
            if (this.elements.shippingInfoShowMore) {
                this.elements.shippingInfoShowMore.textContent =
                    "Info";
            }

            // Shipping tooltip
            const shippingTooltip =
                findElement("info-tooltip");
            if (shippingTooltip) {
                shippingTooltip.innerHTML =
                    this.translations
                        .shipping_info_tooltip ||
                    "Si los necesitas para una fecha específica, por favor, escríbenos a través del chat en línea de nuestra página web o por WhatsApp para hablar directamente con nuestros agentes.";
            }

            // Reposition cost container
            if (
                this.elements.costeReposicionContainer &&
                !window.location.pathname.includes(
                    "pop-mart"
                )
            ) {
                const content = `
            <p style="color: black; font-size: 14px;" id="moreThan500">
                ${
                    this.translations
                        .reposition_cost_message ||
                    "Usted ha seleccionado un artículo con precio superior a 500€. Por ello, le informamos que se aplicará un <strong>coste de reposición</strong> en caso de un producto permitido a cambio o devolución. El detalle del coste visite en"
                }
                <a
                href="https://www.nextstepstore.com/pages/cancellation-change-and-refund"
                target="_blank"
                style="color: blue; text-decoration: underline;"
                >
                ${
                    this.translations.change_refund_page ||
                    "nuestra página de cambios y devoluciones"
                }</a>. 
                ${
                    this.translations.choose_carefully ||
                    "Por favor, elija cuidadosamente el modelo y la talla."
                }
            </p>
            `;
                this.elements.costeReposicionContainer.innerHTML =
                    content;
            } else {
                this.elements.costeReposicionContainer.remove();
            }
        }

        configureShippingInfoBox(selectedValue) {
            // Helper function to find element in document or drawer
            const findElement = (id) => {
                let element = document.getElementById(id);
                if (!element) {
                    const drawerContent =
                        document.querySelector(
                            "#Product-Drawer-Content"
                        );
                    if (drawerContent) {
                        element =
                            drawerContent.querySelector(
                                `#${id}`
                            );
                    }
                }

                return element;
            };

            // Also search for stock info divs in drawer context
            let divsWithStockInfo =
                document.querySelectorAll(
                    "div[stock-info]"
                );

            if (divsWithStockInfo.length === 0) {
                const drawerContent =
                    document.querySelector(
                        "#Product-Drawer-Content"
                    );
                if (drawerContent) {
                    divsWithStockInfo =
                        drawerContent.querySelectorAll(
                            "div[stock-info]"
                        );
                }
            }

            const stockInfo = {};
            divsWithStockInfo.forEach((div) => {
                const info = div.getAttribute("stock-info");
                stockInfo[info.split("-")[0]] =
                    info.split("-")[1];
            });

            const isInStock =
                stockInfo[selectedValue] === "true";

            // Update shipping tooltip based on stock status
            const shippingTooltip =
                findElement("info-tooltip");
            if (shippingTooltip) {
                const tooltipText =
                    this.translations
                        .shipping_info_tooltip ||
                    "Si los necesitas para una fecha específica, por favor, escríbenos a través del chat en línea de nuestra página web o por WhatsApp para hablar directamente con nuestros agentes.";
                shippingTooltip.innerHTML = tooltipText;
            }

            // Find shipping elements dynamically
            const delivery2To14 =
                findElement("delivery2To14");
            const deliveryInfoBox = findElement(
                "deliveryInfoBox"
            );
            const shippingInfoShowMore = findElement(
                "shippingInfoShowMore"
            );

            // Check if this is a special streetwear product
            if (this.isSpecialStreetwear) {
                // Special streetwear products always show 2-8 days delivery
                const specialMessage =
                    window.theme?.translations?.products
                        ?.product?.shipping
                        ?.delivery_2_8_days ||
                    "En camino: será enviado en 2 a 8 días laborales";

                if (delivery2To14) {
                    delivery2To14.innerHTML = specialMessage;
                }
                if (deliveryInfoBox) {
                    // Use standard styling for special delivery
                    deliveryInfoBox.classList.remove(
                        "delivery-info--express",
                        "delivery-info--standard"
                    );
                    deliveryInfoBox.classList.add(
                        "delivery-info--special"
                    );
                }
                if (shippingInfoShowMore) {
                    shippingInfoShowMore.style.display =
                        "inline-flex";
                }
            } else if (isInStock) {
                // Selected variant with stock - show express delivery (green)
                const inStockMessage =
                    window.theme?.translations?.products
                        ?.product?.shipping
                        ?.express_delivery_pickup ||
                    this.translations
                        .express_delivery_pickup ||
                    "En stock: envío en 24 a 48 horas laborales";

                if (delivery2To14) {
                    delivery2To14.innerHTML =
                        inStockMessage;
                }
                if (deliveryInfoBox) {
                    // Switch to express delivery styling (green)
                    deliveryInfoBox.classList.remove(
                        "delivery-info--standard",
                        "delivery-info--special"
                    );
                    deliveryInfoBox.classList.add(
                        "delivery-info--express"
                    );
                }
                if (shippingInfoShowMore) {
                    shippingInfoShowMore.style.display =
                        "none";
                }
            } else {
                // Selected variant without stock - show standard delivery (blue)
                const outOfStockMessage =
                    window.theme?.translations?.products
                        ?.product?.shipping
                        ?.delivery_2_10_days ||
                    this.translations.delivery_2_10_days ||
                    "En camino: envío en 2 a 10 días laborales";

                if (delivery2To14) {
                    delivery2To14.innerHTML =
                        outOfStockMessage;
                }
                if (deliveryInfoBox) {
                    // Switch to standard delivery styling (blue)
                    deliveryInfoBox.classList.remove(
                        "delivery-info--express",
                        "delivery-info--special"
                    );
                    deliveryInfoBox.classList.add(
                        "delivery-info--standard"
                    );
                }
                if (shippingInfoShowMore) {
                    shippingInfoShowMore.style.display =
                        "inline-flex";
                }
            }
        }

        checkVariantPriceAndToggleMessage(radioValue) {
            // Find the variant matching the radioValue
            const selectedVariant = this.productData.find(
                (v) => v.options.includes(radioValue)
            );

            if (!this.elements.costeReposicionContainer) {
                return;
            }

            // If the variant exists and the price is >= €500 (i.e., >= 50000 cents)
            if (
                selectedVariant &&
                selectedVariant.price >= 50000
            ) {
                this.elements.costeReposicionContainer.style.display =
                    "block";
            } else {
                this.elements.costeReposicionContainer.style.display =
                    "none";
            }
        }

        onDOMReady() {
            // Don't set initial text here - let configureShippingInfoBox handle it
            // based on the actual selected variant stock status

            // Listen for variant changes from the main variant selector
            document.addEventListener(
                "product:variant-change",
                (event) => {
                    if (
                        event.detail &&
                        event.detail.variant
                    ) {
                        const variant =
                            event.detail.variant;
                        // Find the option value that matches this variant
                        const optionValue =
                            variant.options[0]; // Assuming first option is size
                        if (optionValue) {
                            this.configureShippingInfoBox(
                                optionValue
                            );
                            this.checkVariantPriceAndToggleMessage(
                                optionValue
                            );
                        }

                        // Ensure add to cart button is enabled for available variants
                        setTimeout(() => {
                            this.ensureAddToCartButtonEnabled();
                        }, 50);
                    }
                }
            );

            // Also listen for direct radio button changes (for drawer context)
            // Use a unique identifier to avoid duplicate listeners
            this.radioChangeHandler = (event) => {
                if (
                    event.target.type === "radio" &&
                    event.target.name &&
                    event.target.value
                ) {
                    const selectedValue =
                        event.target.value;

                    this.configureShippingInfoBox(
                        selectedValue
                    );
                    this.checkVariantPriceAndToggleMessage(
                        selectedValue
                    );

                    setTimeout(() => {
                        this.ensureAddToCartButtonEnabled();
                    }, 50);
                }
            };
            document.addEventListener(
                "change",
                this.radioChangeHandler
            );

            // Initial check for default selected variant
            setTimeout(() => {
                // Find checked radio in both main document and drawer
                let checkedRadio = document.querySelector(
                    "input[type='radio']:checked"
                );
                if (!checkedRadio) {
                    const drawerContent =
                        document.querySelector(
                            "#Product-Drawer-Content"
                        );
                    if (drawerContent) {
                        checkedRadio =
                            drawerContent.querySelector(
                                "input[type='radio']:checked"
                            );
                    }
                }

                if (checkedRadio) {
                    const selectedValue =
                        checkedRadio.value;
                    this.configureShippingInfoBox(
                        selectedValue
                    );
                    this.checkVariantPriceAndToggleMessage(
                        selectedValue
                    );
                }

                // Force enable add to cart button for available variants
                this.ensureAddToCartButtonEnabled();
            }, 100);

            // Hide product description for sneakers
            if (this.isSneaker) {
                const description = document.querySelector(
                    ".product-short-description"
                );
                if (description)
                    description.style.display = "none";
            }
        }

        ensureAddToCartButtonEnabled() {
            // Find the current selected variant in both main document and drawer
            let checkedRadio = document.querySelector(
                "input[type='radio']:checked"
            );
            if (!checkedRadio) {
                const drawerContent =
                    document.querySelector(
                        "#Product-Drawer-Content"
                    );
                if (drawerContent) {
                    checkedRadio =
                        drawerContent.querySelector(
                            "input[type='radio']:checked"
                        );
                }
            }

            if (checkedRadio) {
                const selectedValue = checkedRadio.value;
                const selectedVariant =
                    this.productData.find((v) =>
                        v.options.includes(selectedValue)
                    );

                if (
                    selectedVariant &&
                    selectedVariant.available
                ) {
                    // Find add to cart buttons in both contexts
                    let addToCartButtons =
                        document.querySelectorAll(
                            ".single-add-to-cart-button"
                        );
                    if (addToCartButtons.length === 0) {
                        const drawerContent =
                            document.querySelector(
                                "#Product-Drawer-Content"
                            );
                        if (drawerContent) {
                            addToCartButtons =
                                drawerContent.querySelectorAll(
                                    ".single-add-to-cart-button"
                                );
                        }
                    }

                    addToCartButtons.forEach((button) => {
                        button.removeAttribute("disabled");
                        button.classList.remove("sold-out");
                        const buttonText =
                            button.querySelector(
                                ".single-add-to-cart-button--text"
                            );
                        if (
                            buttonText &&
                            window.theme &&
                            window.theme.variantStrings
                        ) {
                            buttonText.textContent =
                                window.theme.variantStrings
                                    .addToCart ||
                                "Add to cart";
                        }
                    });
                }
            }
        }

        // Cleanup method to remove event listeners
        destroy() {
            if (this.radioChangeHandler) {
                document.removeEventListener(
                    "change",
                    this.radioChangeHandler
                );
            }
            if (this.clickHandler) {
                document.removeEventListener(
                    "click",
                    this.clickHandler
                );
            }
        }
    }

    // Make class globally available
    window.ProductOptionsManager = ProductOptionsManager;
}

// Global variable to store current drawer manager instance
window.drawerProductOptionsManager = null;

// Global initialization function
window.initProductOptions = function (
    productVariants,
    isSneaker = false,
    productTitle = '',
    productTags = []
) {
    new window.ProductOptionsManager(
        productVariants,
        isSneaker,
        productTitle,
        productTags
    );
};

// Listen for quick view open event - simplified approach
document.addEventListener("quick-view:open", function () {
    // Clean up previous drawer manager if exists
    if (window.drawerProductOptionsManager) {
        window.drawerProductOptionsManager.destroy();
        window.drawerProductOptionsManager = null;
    }

    // Clean up any pending ready markers
    if (window.drawerProductOptionsReady) {
        window.drawerProductOptionsReady = null;
    }
});

// Listen for drawer close events to cleanup
document.addEventListener("panel:close", function () {
    if (window.drawerProductOptionsManager) {
        window.drawerProductOptionsManager.destroy();
        window.drawerProductOptionsManager = null;
    }
    // Also clean up any pending ready markers
    if (window.drawerProductOptionsReady) {
        window.drawerProductOptionsReady = null;
    }
});
